/**
 * A signed-out person's recent searches — kept on their own device, and nowhere else.
 *
 * B2 draws three rows under `সাম্প্রতিক`, and the bundle used to ship three constants for
 * them — `বিদ্যুৎ বিল`, `এসি মেরামত`, and a house-rent search in a domain that was not
 * live — shown to every person who opened search as though they had typed them. A signed-in
 * person's recents now come from the Worker (`GET /v1/search/recent`, their own
 * `demand_signals` rows). A stranger has no account for the server to keep them in, so this
 * device is the only place their searches exist, and this is where they are kept.
 *
 * ── WHAT IT KEEPS, AND WHAT IT DOES NOT ───────────────────────────────────────
 * The query text exactly as it was searched, newest first, each once, at most `KEPT`.
 * Nothing about what came back. Written only while nobody is signed in: a signed-in
 * person's history is the server's, and a second copy here would outlive their sign-out
 * on a shared phone.
 *
 * ── STORAGE THAT IS NOT THERE ─────────────────────────────────────────────────
 * A private window, a blocked origin or a full quota make `localStorage` throw or forget.
 * Every read and write is guarded: an unusable store reads as no recents and writes
 * nothing, which is the honest reading of a device that does not remember. The screen
 * then draws no `সাম্প্রতিক` section at all.
 */

/** The one key, named in `auth/session.ts`'s scheme (`oprappo.<area>.<what>.v<n>`). */
export const RECENTS_KEY = 'oprappo.search.recent.v1';

/** How many are kept. B2 draws three; a few more survive a repeated search. */
export const KEPT = 10;

/** How many B2 draws. */
export const SHOWN = 3;

/** The slice of `Storage` this module uses — injectable, because a test has no browser. */
export interface RecentStorage {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
}

function deviceStorage(): RecentStorage | null {
  try {
    const store = (globalThis as { localStorage?: RecentStorage }).localStorage;
    return store ?? null;
  } catch {
    return null;
  }
}

function parse(raw: string | null): string[] {
  if (raw === null) return [];
  try {
    const value: unknown = JSON.parse(raw);
    if (!Array.isArray(value)) return [];
    return value.filter((q): q is string => typeof q === 'string' && q.trim() !== '');
  } catch {
    return [];
  }
}

/** The recents B2 draws, newest first. `[]` when there are none or storage is unusable. */
export function readRecents(storage: RecentStorage | null = deviceStorage()): string[] {
  if (storage === null) return [];
  try {
    return parse(storage.getItem(RECENTS_KEY)).slice(0, SHOWN);
  } catch {
    return [];
  }
}

/**
 * Remember one search, newest first, once. The query is kept as it was sent — trimmed,
 * because the screen trims it, and otherwise untouched.
 */
export function rememberRecent(q: string, storage: RecentStorage | null = deviceStorage()): void {
  const query = q.trim();
  if (query === '' || storage === null) return;
  try {
    const kept = parse(storage.getItem(RECENTS_KEY)).filter((existing) => existing !== query);
    storage.setItem(RECENTS_KEY, JSON.stringify([query, ...kept].slice(0, KEPT)));
  } catch {
    // Quota, a private window, a blocked origin: the device does not remember, and that
    // is all that happened.
  }
}
